import { useEffect, useMemo, useState } from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import CustomNoData from "../../../components/CustomNoData";
import PaginationV2 from "../../../components/pagination_v2";
import StatusBadge from "./StatusBadge";
import VoucherActionsMenu from "./VoucherActionsMenu";

const PAGE_SIZE = 10;

const money = (value) =>
  `${value < 0 ? "-" : ""}${Math.abs(value)} Ks`;

export default function VoucherTable({
  vouchers,
  selectedId,
  onSelect,
  onView,
  onReject,
  rejectingId,
  isLoading,
}) {
  const [page, setPage] = useState(1);
  const totalItems = vouchers.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(currentPage * PAGE_SIZE, totalItems);
  const paginatedVouchers = useMemo(
    () => vouchers.slice(startIndex ? startIndex - 1 : 0, endIndex),
    [endIndex, startIndex, vouchers],
  );

  useEffect(() => {
    setPage(1);
  }, [vouchers]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  return (
    <section className="flex min-h-[510px] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-customBorder dark:bg-custom_dark_primary2">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[650px] text-left text-xs">
          <thead className="bg-[#12264a] text-white">
            <tr>
              {[
                "Voucher",
                "User",
                "Type",
                "Stake",
                "Profit/Loss",
                "Status",
               // "Actions",
              ].map((heading) => (
                <th
                  key={heading}
                  className="whitespace-nowrap px-3 py-3 font-semibold"
                >
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-customBorder/60">
            {paginatedVouchers.map((voucher) => {
              const selected = voucher.id === selectedId;
              return (
                <tr
                  key={voucher.id}
                  onClick={() => onSelect(voucher)}
                  aria-selected={selected}
                  className={`cursor-pointer transition-colors ${selected ? "bg-[#eaf4ff] dark:bg-blue-950/45 dark:bg-custom_dark_primary" : "hover:bg-slate-50 dark:hover:bg-slate-400/20"}`}
                >
                  <td className="whitespace-nowrap px-3 py-2.5 font-semibold text-blue-700 dark:text-blue-300">
                    {voucher.id}
                  </td>
                  <td className="px-3 py-2.5">{voucher.user}</td>
                  <td className="whitespace-nowrap px-3 py-2.5">
                    <span className="font-medium">{voucher.type}</span> ({voucher.teams})
                  </td>
                  <td className="px-3 py-2.5 font-medium">
                    {money(voucher.stake)}
                  </td>
                  <td
                    className={`px-3 py-2.5 font-semibold ${voucher.result > 0 ? "text-emerald-700 dark:text-emerald-400" : voucher.result < 0 ?"text-rose-700 dark:text-rose-400" : "text-slate-700 dark:text-slate-400"}`}
                  >
                    {money(voucher.result.toFixed(3))}
                  </td>
                  <td className="px-3 py-2.5">
                    <StatusBadge status={voucher.status} />
                  </td>
                  {/* <td className="px-3 py-2.5">
                    <VoucherActionsMenu
                      voucher={voucher}
                      onView={onView}
                      onReject={onReject}
                      isRejecting={rejectingId === voucher.id}
                    />
                  </td> */}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isLoading && (
        <div className="flex flex-1 items-center justify-center gap-2 py-16 text-sm font-medium text-slate-500">
          <ArrowPathIcon className="h-5 w-5 animate-spin text-customAccentBlue" />
          Loading vouchers…
        </div>
      )}

      {!isLoading && totalItems === 0 && (
        <CustomNoData
          title="No vouchers found"
          description="No vouchers match the selected filters."
          py={16}
          className="m-4 flex-1"
        />
      )}

      {!isLoading && (
        <div className="mt-auto">
          <PaginationV2
            startIndex={startIndex}
            endIndex={endIndex}
            totalItems={totalItems}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setPage}
            itemLabel="vouchers"
          />
        </div>
      )}
    </section>
  );
}
